import { RECEIVER_TYPE } from "@icoach/constants/dataEnums";
import { isEmpty } from "@util/utils";
import { receiverTypeMap } from "@icoach/accountBook/staticData";

/**
 * 接收者類型是否需要特殊佈局處理
 */
export const RECEIVER_LAYOUT_CONFIG = {
	[RECEIVER_TYPE.CROSSMEMBER]: {
		buttonAlign: "ml-auto", // 按鈕靠右對齊
	},
};

/**
 * 判斷當前菜單是否為 "全商品搜尋" 菜單
 * @param {Object} menu - 當前選中的菜單項目
 * @returns {boolean}
 */
export const isAllMerchandiseMenu = menu => {
	// 隱性規則轉為顯性：ALL 菜單的 value 是前端定義的 "ALL"
	return menu && menu.value === "ALL";
};

/**
 * 判斷當前菜單是否為 "費用類別" 菜單
 * @param {Object} menu - 當前選中的菜單項目
 * @returns {boolean}
 */
export const isFeeCategory = menu => {
	// 隱性規則轉為顯性：費用類別的特徵是它有 checkoutItems 且不為空
	return menu && !isEmpty(menu.checkoutItems);
};

/**
 * 轉義正則表達式特殊字符
 * @param {string} str - 要轉義的字串
 * @returns {string} 轉義後的字串
 */
export const escapeRegexSpecialChars = str => {
	return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

/**
 * 創建搜尋正則表達式
 * @param {string} keyword - 搜尋關鍵字
 * @returns {RegExp} 正則表達式對象
 */
export const createSearchRegex = keyword => {
	const escapedKeyword = escapeRegexSpecialChars(keyword);
	return new RegExp(escapedKeyword, "i"); // 不區分大小寫
};

/**
 * 檢查是否為商品項目
 * 根據是否有 productID 屬性來判斷（這是區分商品和費用的關鍵）
 * @param {Object} item - 要檢查的項目
 * @returns {boolean} 如果是商品項目返回 true，否則返回 false
 */
export const isProductItem = item => {
	return item && item.hasOwnProperty("productID") && item.productID !== undefined && item.productID !== null && item.productID !== 0;
};

/**
 * 根據關鍵字過濾商品和費用列表
 * 搜尋範圍包括：
 * - 商品：商品名稱、規格名稱、SKU
 * - 費用：費用名稱 (text)
 * @param {Array} items - 商品/費用列表
 * @param {string} keyword - 搜尋關鍵字
 * @returns {Array} 過濾後的商品/費用列表
 */
export const filterItemsByKeyword = (items, keyword) => {
	if (!items || !Array.isArray(items)) {
		return [];
	}

	// 如果關鍵字為空，返回所有項目
	if (!keyword || !keyword.trim()) {
		return items;
	}

	const trimmedKeyword = keyword.trim().toLowerCase();
	const searchRegex = createSearchRegex(trimmedKeyword);

	return items.filter(item => {
		// 判斷是商品還是費用
		if (isProductItem(item)) {
			// 商品：搜尋商品名稱、規格名稱、SKU
			if (item.productName && searchRegex.test(item.productName)) {
				return true;
			}

			if (item.variantName && searchRegex.test(item.variantName)) {
				return true;
			}

			if (item.sku && searchRegex.test(item.sku)) {
				return true;
			}
		} else {
			// 費用：搜尋費用名稱 (text)
			if (item.text && searchRegex.test(item.text)) {
				return true;
			}
		}

		return false;
	});
};

/**
 * @deprecated 請使用 filterItemsByKeyword，此函數保留僅為向後兼容
 * 根據關鍵字過濾商品和費用列表
 */
export const filterProductsByKeyword = filterItemsByKeyword;

/**
 * 取得產品類品項資料（用於 API 提交）
 * @param {Object} item - 購物車中的產品項目
 * @returns {Object} 格式化後的產品資料
 */
export const getProductItemData = item => {
	const { productID, variantID, assignPrice, quantity, notes, discountPrice } = item;
	return {
		productID,
		variantID,
		assignPrice,
		quantity,
		notes,
		...(discountPrice !== undefined && discountPrice !== null ? { discountPrice } : {}),
	};
};

/**
 * 取得費用品項資料（用於 API 提交）
 * @param {Object} item - 購物車中的費用項目
 * @returns {Object} 格式化後的費用資料
 */
export const getFeeItemData = item => {
	const { revenueType, name, assignPrice, notes, discountPrice } = item;
	return {
		revenueType,
		name,
		assignPrice,
		notes,
		...(discountPrice !== undefined && discountPrice !== null ? { discountPrice } : {}),
	};
};

/**
 * 格式化購物車結果資料（用於 API 提交）
 * 會自動過濾掉數量為0的商品
 * @param {Array} cartItems - 購物車資料陣列
 * @param {boolean} filterZeroQuantity - 是否過濾數量為0的商品（預設為 true）
 * @returns {Object} { items: Array, products: Array }
 */
export const formatCartResult = (cartItems, filterZeroQuantity = true) => {
	let items = [],
		products = [];

	cartItems.forEach(item => {
		if (!item) return;

		// 過濾數量為0的商品（如果啟用過濾）
		if (filterZeroQuantity && isProductItem(item) && Number(item.quantity) === 0) {
			return; // 跳過數量為0的商品
		}

		if (isProductItem(item)) {
			products.push(getProductItemData(item));
		} else {
			items.push(getFeeItemData(item));
		}
	});

	return {
		items,
		products,
	};
};

/**
 * 將後端商品資料轉換為購物車格式
 * @param {Object} backendItem - 後端返回的商品資料
 * @param {Object} options - 選項配置
 * @param {boolean} options.allowDelete - 是否允許刪除（預設為 true）
 * @param {boolean} options.allowZeroQuantity - 是否允許數量為0（預設為 false）
 * @returns {Object} 購物車商品項目
 */
export const convertBackendProductToCartItem = (backendItem, options = {}) => {
	const { allowDelete = true, allowZeroQuantity = false } = options;

	return {
		productID: backendItem.productID,
		variantID: backendItem.variantID,
		productName: backendItem.productName,
		variantName: backendItem.variantName || "",
		sku: backendItem.sku || "",
		assignPrice: backendItem.unitPrice || backendItem.price || 0,
		quantity: backendItem.quantity || 0,
		productTypeText: backendItem.productTypeText || "",
		stock: backendItem.stock,
		isLimited: backendItem.isLimited,
		notes: backendItem.notes || "",
		allowDelete,
		allowZeroQuantity,
	};
};

/**
 * 將後端返回的訂單 items 轉換為 OrderCartList 可使用的格式
 * 支援商品項目和費用項目的轉換
 * @param {Array} backendItems - 後端返回的 items 陣列
 * @returns {Array} 轉換後的購物車項目陣列
 */
export const convertBackendOrderItemsToCartItems = backendItems => {
	if (!Array.isArray(backendItems)) {
		return [];
	}

	return backendItems.map(item => {
		const { productID, productName, variantID, variantName, assignPrice, quantity, productTypeText } = item;

		// 使用 isProductItem 判斷是否為商品項目
		if (isProductItem(item)) {
			// 商品項目轉換
			return {
				productID,
				variantID: variantID || 0,
				productName: productName || "",
				variantName: variantName || "",
				assignPrice: assignPrice || 0,
				quantity: quantity || 0,
				sku: "", // 會請後端補
				productTypeText: productTypeText || "",
				notes: "", // 會請後端補
			};
		} else {
			// 費用項目轉換
			return {
				name: productName || "",
				assignPrice: assignPrice || 0,
				productTypeText: productTypeText || "",
				notes: "", // 會請後端補
			};
		}
	});
};

/**
 * 計算訂單總額（純函數，不更新狀態）
 * @param {Object} refs - 子組件的 refs
 * @returns {number} 訂單總額
 */
export const calculateOrderTotalAmount = refs => {
	const { newOrderCartRef, unpaidOrderRef } = refs;
	let total = 0;

	// 計算新訂單購物車總額
	if (newOrderCartRef?.current?.getTotal) {
		const cartTotal = newOrderCartRef.current.getTotal();
		total += cartTotal || 0;
	}

	// 計算賒帳訂單總額
	if (unpaidOrderRef?.current?.getTotalData) {
		const data = unpaidOrderRef.current.getTotalData();
		total += data?.total || 0;
	}

	return total;
};

/**
 * 全商品選項配置（共用）
 */
export const allMerchandiseData = {
	checkoutItems: [], // 既存月費 value: "201"
	children: null,
	disabled: false,
	text: "全商品",
	value: "ALL",
};

/**
 * 根據接收者類型解析使用的商品選單配置（共用）
 * @param {Object} resource - 資源配置物件
 * @param {Function} getReceiver - 取得接收者類型的函數
 * @returns {Array} 商品選單陣列
 */
export const determineMerchandiseOptions = (resource, getReceiver) => {
	const { forMemberOptions = [], otherOptions = [] } = resource;

	// 會員類型接收者使用會員專屬選單
	const memberMerchandiseOpt = [allMerchandiseData, ...forMemberOptions];
	// 其他類型接收者使用相同選單
	const otherMerchandiseOpt = [allMerchandiseData, ...otherOptions];

	// 根據接收者類型映射到對應的商品選單
	const shoppingMenuMap = new Map([
		[receiverTypeMap.member, memberMerchandiseOpt],
		[receiverTypeMap.nonMember, otherMerchandiseOpt],
		[receiverTypeMap.otherClubMember, otherMerchandiseOpt],
		[receiverTypeMap.employee, otherMerchandiseOpt],
	]);

	// 取得當前接收者類型
	const receiverType = String(getReceiver());

	return shoppingMenuMap.get(receiverType) || [];
};
